import { MutableRefObject } from "react";
import { DateTime } from "luxon";

/**
 * Determines pre-fill comp amount when closing a job
 * For closed jobs: Uses the existing hiredCompensationAmount (in cents)
 * For new closures: Converts totalAnticipated from dollars to cents
 */
export const getJobPostCompensationAmount = (jobPost: any): string | null => {
  if (jobPost.hiredCompensationAmount !== null) {
    return jobPost.hiredCompensationAmount;
  }

  return jobPost.compensation?.totalAnticipated ? jobPost.compensation.totalAnticipated.toFixed(2).toString() : null;
};

export interface JobPostCloseFormInput {
  reason: string;
  notes?: string;
  closeDate: string;
  hiringStatus: "Filled" | "Incomplete";
  careerConsultantVisited: "Not Set" | "Yes" | "No";
  isPgaFacilityConversion: "Not Set" | "Yes" | "No";
  isPgaJobPlacementConversion: "Not Set" | "Yes" | "No";
  hireIsPgaPro: "Not Set" | "Not Sure" | "Yes" | "No";
  /** Net-new position at the facility vs backfill; backend `is_net_new_position` */
  isNetNewPosition: "Not Set" | "Yes" | "No";
  hireCompensationAmount: string | null;
  hireCompensationSchedule?: string;
  previousCompensation?: string | null;
  sendEmployerSurvey: boolean;
  hireeId: string;
  employeeData: any;
  employerData: any;
}

export interface JobPostCloseFormProps {
  isEdit?: boolean;
  defaultValues?: JobPostCloseFormInput;
  onSubmit?: (data: JobPostCloseFormInput) => Promise<void>;
  formRef?: MutableRefObject<{ getValues: () => JobPostCloseFormInput } | null>;
  submitDialogContent?: string;
  submitText?: string;
  jobPostId: string;
  sectionId?: string;
  onSuccess?: (id: string) => void;
}

/**
 * Creates default values for the JobPostCloseForm
 * @param jobPost Optional job post data to initialize with
 * @returns Default JobPostCloseFormInput values
 */
export const getDefaultJobPostCloseFormValues = (jobPost?: any): JobPostCloseFormInput => {
  const defaultValues: JobPostCloseFormInput = {
    reason: "Filled",
    notes: "",
    closeDate: DateTime.now().toFormat("yyyy-MM-dd"),
    hiringStatus: "Filled",
    careerConsultantVisited: "Not Set",
    isPgaFacilityConversion: "Not Set",
    isPgaJobPlacementConversion: "Not Set",
    hireIsPgaPro: "Not Set",
    isNetNewPosition: "Not Set",
    hireCompensationAmount: null,
    hireCompensationSchedule: "Salary",
    previousCompensation: null,
    sendEmployerSurvey: true,
    hireeId: "",
    employeeData: null,
    employerData: null
  };

  if (!jobPost) return defaultValues;

  return {
    ...defaultValues,
    notes: jobPost.closeComments || "",
    careerConsultantVisited: jobPost.consultantVisitedFacility || "Not Set",
    isPgaFacilityConversion: jobPost.isFacilityConversion || "Not Set",
    isPgaJobPlacementConversion: jobPost.isPlacementConversion || "Not Set",
    hireIsPgaPro: jobPost.isHireePgaPro || "Not Sure",
    isNetNewPosition: jobPost.isNetNewPosition || "Not Set",
    hireCompensationAmount: getJobPostCompensationAmount(jobPost),
    hireCompensationSchedule: jobPost.paySchedule || "Salary",
    previousCompensation: jobPost.previousCompensation || null,
    sendEmployerSurvey: jobPost.sendEmployerSurvey ?? true,
    hireeId: jobPost.hiree?.id || "",
    employeeData: jobPost.hiree || null
  };
};
